import { Download, Eye, Heart } from "lucide-react";
import { capitalise } from "../utils/text";

/**
 * One artwork card, showing the real pixel dimensions captured during sync.
 */

const formatDimensions = (asset) => {
  if (!asset.width || !asset.height) return null;
  return `${asset.width} x ${asset.height} px`;
};

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!bytes || Number.isNaN(bytes)) return null;
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
};

function AssetCard({
  asset,
  imageUrl,
  onView,
  onDownload,
  onToggleFavorite,
  showDownloadCount = false,
  downloadDisabled = false,
}) {
  const title = capitalise(asset.file_name) || "Untitled print";
  const dimensions = formatDimensions(asset);
  const fileSize = formatBytes(asset.file_size);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onView();
    }
  };

  return (
    <article
      className="asset-card-shell"
      tabIndex="0"
      onClick={onView}
      onKeyDown={handleKeyDown}
      aria-label={`Open ${title}`}
    >
      <div className="asset-swatch">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="asset-image"
            loading="lazy"
            // Reserving the real aspect ratio stops the masonry column from
            // reflowing as each thumbnail arrives.
            width={asset.width || undefined}
            height={asset.height || undefined}
          />
        ) : (
          <div className="asset-image-missing" aria-hidden="true" />
        )}

        <div className="asset-action-bar" aria-label={`${title} actions`}>
          <button
            type="button"
            className={`asset-icon-button${asset.is_favorite ? " is-favorite" : ""}`}
            onClick={(event) => {
              event.stopPropagation();
              onToggleFavorite();
            }}
            aria-pressed={Boolean(asset.is_favorite)}
            title={asset.is_favorite ? "Remove from favourites" : "Add to favourites"}
          >
            <Heart
              size={15}
              strokeWidth={1.7}
              fill={asset.is_favorite ? "currentColor" : "none"}
              aria-hidden="true"
            />
            <span className="sr-only">
              {asset.is_favorite ? "Remove from favourites" : "Add to favourites"}
            </span>
          </button>
          <button
            type="button"
            className="asset-icon-button"
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
          >
            <Eye size={15} strokeWidth={1.7} aria-hidden="true" />
            <span className="sr-only">View</span>
          </button>
          <button
            type="button"
            className="asset-icon-button"
            onClick={(event) => {
              event.stopPropagation();
              onDownload();
            }}
            disabled={downloadDisabled || !asset.file_id}
          >
            <Download size={15} strokeWidth={1.7} aria-hidden="true" />
            <span className="sr-only">Download</span>
          </button>
        </div>

        <div className="asset-ticket">
          <div className="asset-ticket-copy">
            <p className="asset-ticket-label">{capitalise(asset.category)}</p>
            <h3>{title}</h3>
            <p className="asset-ticket-meta">
              {[dimensions, fileSize].filter(Boolean).join("  /  ") || "—"}
            </p>
          </div>
          <div className="asset-ticket-stats">
            <span title={`${asset.views || 0} views`}>
              <Eye size={13} strokeWidth={1.7} aria-hidden="true" />
              {asset.views || 0}
            </span>
            {showDownloadCount && (
              <span title={`${asset.download_count || 0} downloads`}>
                <Download size={13} strokeWidth={1.7} aria-hidden="true" />
                {asset.download_count || 0}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default AssetCard;
